import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { GET_PROJECT_LIVE_BRAND_KEYS } from '../../../query/project/stats/keys';
import { ChatType } from '../../../../types/monitor/ChatType';
import { MonitorPrompt } from '../../../../models/monitor/MonitorPrompt';
import { getProjectLiveBrandKeys } from '../../../services/project/stats/ProjectDailyStatsService';

/**
 * Tag-resolved keys of the project's "live" brands (have data under the account's current models),
 * independent of the project_options.keywords selection. Used by the brand-selection surfaces
 * (ChooseKeywords dialog, ChangeKeywordBox) to hide orphan brands from their option lists.
 */
export const useGetLiveBrandKeys = (
  projectId: number | null,
  filteredMonitorPrompts: MonitorPrompt[] | null,
  chatType: ChatType | null,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[]
) => {
  const queryClient = useQueryClient();
  const hasPrompts = !!filteredMonitorPrompts && filteredMonitorPrompts.length > 0;
  const queryEnabled = !!projectId && !!chatType && !!from && !!to && hasPrompts;

  const monitorPromptIds = useMemo(
    () => filteredMonitorPrompts?.map((p) => p.id).filter((id): id is number => id != null),
    [filteredMonitorPrompts]
  );

  const queryKey = [
    GET_PROJECT_LIVE_BRAND_KEYS,
    projectId,
    chatType,
    from,
    to,
    monitorPromptIds,
    filterChatTypes,
  ];

  const {
    data: liveBrandKeys,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<string[]>({
    queryKey,
    queryFn: () =>
      getProjectLiveBrandKeys(projectId!, chatType!, from, to, filterChatTypes, monitorPromptIds),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
  });

  return {
    liveBrandKeys: queryEnabled ? liveBrandKeys ?? [] : [],
    isLoading: queryEnabled ? isLoading : false,
    isError: queryEnabled ? isError : false,
    error: queryEnabled ? error : null,
    refetch,
    invalidate: () => queryClient.invalidateQueries({ queryKey, refetchType: 'active' }),
  };
};
